// The dining API client (docs/DINING_API_2026-10-28.md, docs/CHECKOUT_API_2026-10-28.md).
// Every screen shows what the server answers - nothing is assumed to have
// worked until the answer says so.
import { API } from '@/config/api';
import { getAuthToken } from '@/lib/authToken';

export class ApiError extends Error {
  constructor(status, body, fallback) {
    const detail = body?.detail;
    const message = typeof detail === 'string' ? detail
      : (detail && typeof detail.message === 'string' ? detail.message
        : (Array.isArray(detail) ? detail.map((d) => d?.msg).filter(Boolean).join('; ') : null))
      || body?.message || fallback || `Request failed (${status})`;
    super(message);
    this.status = status;
    this.body = body;
    this.detail = detail;
    this.code = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail.code || null : null;
  }

  get isApprovalRequired() { return this.status === 403 && this.code === 'approval_required'; }

  get isStale() { return this.status === 409 && this.code === 'stale_version'; }

  get isSignedOut() { return this.status === 401; }
}

export function deviceId() {
  let id = null;
  try { id = localStorage.getItem('pos_device_id'); } catch (e) { id = null; }
  if (!id) {
    id = `dining_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    try { localStorage.setItem('pos_device_id', id); } catch (e) { /* private mode */ }
  }
  return id;
}

export function newKey(prefix = 'c29') {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rand}`;
}

// Whose sign-in the calls carry: the staff PIN / register sign-in (default),
// or a PWA's restaurant account (the Kitchen PWA, apps/kds) - that app sets
// it once with setAuthSource({getToken, merchantId}) before any screen loads.
let authSource = null;

export function setAuthSource(source) {
  authSource = source || null;
}

export function authToken() {
  return authSource ? authSource.getToken() : getAuthToken();
}

// X-Merchant-ID for the account's chosen location (null: the token's own restaurant)
export function authMerchantId() {
  return authSource && authSource.merchantId ? authSource.merchantId() : null;
}

export function hasToken() {
  return !!authToken();
}

export async function request(method, path, { body, query, idempotencyKey, signal } = {}) {
  const token = authToken();
  const headers = { Accept: 'application/json', 'X-Device-ID': deviceId() };
  if (token) headers.Authorization = `Bearer ${token}`;
  const merchantId = authMerchantId();
  if (merchantId) headers['X-Merchant-ID'] = String(merchantId);
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const qs = query
    ? `?${Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`
    : '';
  let response;
  try {
    response = await fetch(`${API}${path}${qs === '?' ? '' : qs}`, {
      method, headers, body: body === undefined ? undefined : JSON.stringify(body), signal,
    });
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const err = new ApiError(0, null, offline ? 'This device is offline.' : 'Could not reach chain29. Check the connection and try again.');
    err.network = true;
    throw err;
  }
  let data = null;
  try { data = await response.json(); } catch (e) { data = null; }
  if (!response.ok) throw new ApiError(response.status, data);
  return data;
}

export const api = {
  get: (path, query, opts) => request('GET', path, { query, ...opts }),
  post: (path, body = {}, opts) => request('POST', path, { body, ...opts }),
  patch: (path, body = {}, opts) => request('PATCH', path, { body, ...opts }),
  del: (path, query, opts) => request('DELETE', path, { query, ...opts }),
};

export function errorText(error, fallback = 'Something went wrong.') {
  if (!error) return fallback;
  return error.message || fallback;
}
